/**
 * Pure domain logic and catalog definitions for DC automation scenarios.
 */

export type ScenarioStatus = 'available' | 'in_development' | 'planned';

export type ScenarioCategory = 'impact' | 'capacity' | 'thermal' | 'migration' | 'connectivity';

export interface ScenarioItem {
  id: string;
  title: string;
  description: string;
  status: ScenarioStatus;
  statusLabel: string;
  category: ScenarioCategory;
  categoryLabel?: string;
  howToUse?: string;
  linkPath?: string;
  linkLabel?: string;
}

export const SCENARIOS_REGISTRY: ScenarioItem[] = [
  {
    id: 'equipment-swap',
    title: 'Замена оборудования (Impact Analysis)',
    description: 'Анализ последствий замены устройства на другую модель: портовая емкость, совместимость разъемов, длина кабелей и габариты.',
    status: 'available',
    statusLabel: 'Доступен',
    category: 'impact',
    categoryLabel: 'Impact Analysis',
    howToUse: 'В разделе «Оборудование» выберите устройство и нажмите «Заменить (Impact Analysis)».',
    linkPath: '/equipment',
    linkLabel: 'Перейти к Оборудованию',
  },
  {
    id: 'thermal-what-if',
    title: 'Тепловое What-If моделирование',
    description: 'Симуляция теплового шлейфа (Gaussian plume diffusion) и оценка нагрузки на систему кондиционирования при изменении мощности оборудования.',
    status: 'available',
    statusLabel: 'Доступен',
    category: 'thermal',
    categoryLabel: 'Тепловое моделирование',
    howToUse: 'В редакторе плана этажа («Редактор ЦОД») активируйте тепловой слой What-If для моделирования градиента температур.',
    linkPath: '/floor-plan',
    linkLabel: 'Открыть План Этажа (What-If)',
  },
  {
    id: 'capacity-planning',
    title: 'Планирование емкости стоек и питания',
    description: 'Сценарный мониторинг заполнения стоек по высоте (U-юниты), предельным нагрузкам электропитания (кВт, порог NEC 80%) и PDU-фидерам.',
    status: 'available',
    statusLabel: 'Доступен',
    category: 'capacity',
    categoryLabel: 'Планирование емкости',
    howToUse: 'Откройте модуль «Электропитание» для анализа лимитов стоек и PDU или выберите стойку в «Списке стоек».',
    linkPath: '/power',
    linkLabel: 'Анализ Мощности и PDU',
  },
  {
    id: 'cable-trace-routing',
    title: 'Сквозная трассировка кабельных линий',
    description: 'Многозвенная графовая трассировка физических соединений между патч-панелями, серверами и коммутаторами с анализом промежуточных узлов.',
    status: 'available',
    statusLabel: 'Доступен',
    category: 'connectivity',
    categoryLabel: 'Связность и кабели',
    howToUse: 'В модуле «Связность» выберите порты или используйте фасадную панель оборудования для мгновенной трассировки.',
    linkPath: '/connectivity',
    linkLabel: 'Перейти к Связности',
  },
  {
    id: 'rack-migration',
    title: 'Пакетная миграция серверов',
    description: 'Оркестрация переноса групп серверов между стойками и машзалами с автоматической генерацией нарядов на демонтаж, монтаж и перекоммутацию.',
    status: 'in_development',
    statusLabel: 'В разработке',
    category: 'migration',
    categoryLabel: 'Миграция оборудования',
    howToUse: 'Генератор нарядов доступен в модуле задач через AI Work Order.',
    linkPath: '/tasks',
    linkLabel: 'Открыть Наряды и Задачи',
  },
  {
    id: 'ai-autonomous-rebalancing',
    title: 'Автономная оптимизация размещения и охлаждения',
    description: 'Нейросетевой расчет перераспределения вычислительной нагрузки для ликвидации локальных точек перегрева (hotspots) и оптимизации PUE.',
    status: 'planned',
    statusLabel: 'Запланирован',
    category: 'thermal',
    categoryLabel: 'Тепловое моделирование',
  },
];

export function getScenarioStatusBadgeClass(status: ScenarioStatus): { bg: string; text: string; border: string } {
  switch (status) {
    case 'available':
      return { bg: 'bg-emerald-950/60', text: 'text-emerald-400', border: 'border-emerald-800/80' };
    case 'in_development':
      return { bg: 'bg-amber-950/60', text: 'text-amber-400', border: 'border-amber-800/80' };
    case 'planned':
    default:
      return { bg: 'bg-slate-900', text: 'text-slate-400', border: 'border-slate-800' };
  }
}

export function getScenarioCategoryLabel(category: ScenarioCategory): string {
  switch (category) {
    case 'impact':
      return 'Impact Analysis';
    case 'thermal':
      return 'Тепловое моделирование';
    case 'capacity':
      return 'Планирование емкости';
    case 'migration':
      return 'Миграция оборудования';
    case 'connectivity':
      return 'Связность и кабели';
    default:
      return category;
  }
}

export function getScenarioCounts(items: ScenarioItem[]): {
  total: number;
  available: number;
  in_development: number;
  planned: number;
} {
  const counts = { total: 0, available: 0, in_development: 0, planned: 0 };
  if (!Array.isArray(items)) return counts;

  counts.total = items.length;
  for (const item of items) {
    if (item.status === 'available') counts.available++;
    else if (item.status === 'in_development') counts.in_development++;
    else if (item.status === 'planned') counts.planned++;
  }
  return counts;
}

export interface ScenarioFilterOptions {
  status?: ScenarioStatus | 'all';
  category?: ScenarioCategory | 'all';
  query?: string;
}

export function filterScenarios(
  items: ScenarioItem[],
  options: ScenarioFilterOptions = {}
): ScenarioItem[] {
  if (!Array.isArray(items)) return [];

  const { status, category, query } = options;
  const q = (query || '').trim().toLowerCase();

  return items.filter(item => {
    if (status && status !== 'all' && item.status !== status) {
      return false;
    }
    if (category && category !== 'all' && item.category !== category) {
      return false;
    }
    if (q) {
      const matchTitle = item.title.toLowerCase().includes(q);
      const matchDesc = item.description.toLowerCase().includes(q);
      const matchCat = (item.categoryLabel || item.category).toLowerCase().includes(q);
      const matchHow = item.howToUse ? item.howToUse.toLowerCase().includes(q) : false;
      if (!matchTitle && !matchDesc && !matchCat && !matchHow) {
        return false;
      }
    }
    return true;
  });
}

export function filterScenariosByStatus(
  items: ScenarioItem[],
  statusFilter?: ScenarioStatus | 'all'
): ScenarioItem[] {
  return filterScenarios(items, { status: statusFilter });
}

